import * as styles from './index.css';
import { CustomizeSidebarModal } from './customize-sidebar-modal';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import type { NavItem as NavItemType, NavItemGroup } from './routes';
import { generateRoutes, isNavItemActive } from './routes';
import { isEmptyString } from '@/utils/string';

import { shouldProcessLinkClick } from '../../../../utils/navigation';
import { useIsSettings } from '@/services/router/use-is-settings';

import { useListWorkspaceObjectTypes } from '@/features/workspaces';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';
import { useUpdateUserSettings } from '@/services/db/user-settings/mutations';
import {
  SideNavGroup,
  NavItem,
  NavItemExpandable,
  NavItemExpandableTrigger,
  NavItemExpandableContent,
  NavItemDropdown,
  NavItemDropdownTrigger,
  NavItemDropdownContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  Box,
  Label,
  InlineStack,
  Tooltip,
  useModals,
  useSnackbars,
  useSideNavState,
} from '@customerio/pluma-components/react';
import { getPresentationNavItemGroups, getSideNavPreferenceData } from './side-nav-preferences';

type NavItemClickHandler = (
  event: React.MouseEvent | undefined,
  item: NavItemType,
  section: string | null,
  parentItem: string | null,
) => unknown;

type ExtendedNavItemProps = {
  item: NavItemType;
  onClick: NavItemClickHandler;
  section: string | null;
  parentItem?: string | null;
};

type NavItemLabelContentProps = {
  item: Pick<NavItemType, 'label' | 'showNewLabel' | 'showPremiumLabel'>;
};

function NavItemLabelContent({ item }: NavItemLabelContentProps) {
  const { label, showNewLabel = false, showPremiumLabel = false } = item;

  return (
    <InlineStack gap="050" alignBlock="center">
      <Box as="span" truncate={true}>
        {label}
      </Box>
      {showNewLabel ? (
        <Label color="purple" flexShrink="0">
          New
        </Label>
      ) : null}
      {showPremiumLabel ? (
        <Label color="plum" flexShrink="0">
          Premium
        </Label>
      ) : null}
    </InlineStack>
  );
}

export function ExtendedNavItem(props: ExtendedNavItemProps) {
  const { item, onClick, section, parentItem = null } = props;

  const activeRoute = useStore(activeRouteStore);

  const {
    iconName,
    href,
    label,
    id,
    isGreyedOut = false,
    isDisabled = false,
    isVisuallyDisabled = false,
    disabledTooltip,
    children,
    ariaLabel,
  } = item;

  if (item.isDropdownMenu === true && children != null) {
    if (children.length === 0) {
      return null;
    }

    if (children.length === 1) {
      const child = {
        ...children[0],
        isVisibleWhenDeselected: item.isVisibleWhenDeselected,
        isVisibleWhenSelected: item.isVisibleWhenSelected,
      };

      return <ExtendedNavItem item={child} onClick={onClick} section={section} parentItem={item.label} />;
    }

    return (
      <NavItemDropdown>
        <NavItemDropdownContent>
          {children.map((child) => (
            <ExtendedNavItem key={child.id} item={child} onClick={onClick} section={section} parentItem={item.label} />
          ))}
        </NavItemDropdownContent>

        <NavItemDropdownTrigger
          aria-label={ariaLabel}
          icon={iconName}
          isVisibleWhenDeselected={item.isVisibleWhenDeselected}
          isVisibleWhenSelected={item.isVisibleWhenSelected}
        >
          {label}
        </NavItemDropdownTrigger>
      </NavItemDropdown>
    );
  }

  if (children != null) {
    const hasSelectedChild = children.some((child) => isNavItemActive(child, activeRoute));

    return (
      <NavItemExpandable key={item.id} defaultIsOpen={item.defaultIsOpen}>
        <NavItemExpandableTrigger
          icon={iconName}
          hasSelectedChild={hasSelectedChild}
          isVisibleWhenDeselected={item.isVisibleWhenDeselected}
          isVisibleWhenSelected={item.isVisibleWhenSelected}
        >
          {label}
        </NavItemExpandableTrigger>

        <NavItemExpandableContent>
          {children.map((child) => {
            return (
              <ExtendedNavItem
                key={child.id}
                item={child}
                onClick={onClick}
                section={section}
                parentItem={item.label}
              />
            );
          })}
        </NavItemExpandableContent>
      </NavItemExpandable>
    );
  }

  const navItem = (
    <NavItem
      data-test-nav-item={id}
      href={isDisabled ? undefined : href}
      icon={iconName}
      isVisibleWhenDeselected={item.isVisibleWhenDeselected}
      isVisibleWhenSelected={item.isVisibleWhenSelected}
      isSelected={isNavItemActive(item, activeRoute)}
      onClick={(event) => {
        if (isDisabled) {
          event?.preventDefault();
          return;
        }
        onClick(event, item, section, parentItem);
      }}
      aria-disabled={isDisabled ? true : undefined}
      tabIndex={isDisabled ? -1 : undefined}
    >
      <NavItemLabelContent item={item} />
    </NavItem>
  );

  if (isDisabled) {
    if (!isEmptyString(disabledTooltip)) {
      return (
        <Tooltip content={disabledTooltip} placement="right" isCustom={true}>
          {(context) => (
            <div
              className={styles.disabledWrapper}
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
            >
              {navItem}
            </div>
          )}
        </Tooltip>
      );
    }
    return <div className={styles.disabledWrapper}>{navItem}</div>;
  }

  if (isVisuallyDisabled) {
    return <div className={styles.visuallyDisabled}>{navItem}</div>;
  }

  if (isGreyedOut) {
    return <div className={styles.greyedOut}>{navItem}</div>;
  }

  return navItem;
}

type MoreMenuNavItemProps = {
  item: NavItemType;
  onClick: NavItemClickHandler;
  section: string | null;
  parentItem: string | null;
};

function MoreMenuNavItem(props: MoreMenuNavItemProps) {
  const { item, onClick, section, parentItem } = props;
  const { href, iconName, isDisabled = false } = item;

  return (
    <DropdownMenuItem
      href={isDisabled ? undefined : href}
      icon={iconName}
      isDisabled={isDisabled ? true : undefined}
      onClick={(event) => {
        if (isDisabled) {
          event?.preventDefault();
          return;
        }
        onClick(event, item, section, parentItem);
      }}
      tooltip={item.disabledTooltip}
    >
      <NavItemLabelContent item={item} />
    </DropdownMenuItem>
  );
}

export function MainNavigation() {
  const currentWorkspace = useCurrentWorkspace();
  const permissions = usePermissions();
  const auth = useAuth();
  const taxonomy = useProductTaxonomy();
  const safeNavigate = useSafeNavigate();
  const journeysObjectTypes = useListWorkspaceObjectTypes(currentWorkspace?.id);
  const { track } = useAnalytics();
  const modals = useModals();
  const snackbars = useSnackbars();
  const sideNavState = useSideNavState();
  const activeRoute = useStore(activeRouteStore);
  const isCollapsedRef = useRef(sideNavState?.isCollapsed === true);
  isCollapsedRef.current = sideNavState?.isCollapsed === true;

  const { isSettingsPage } = useIsSettings();
  const sideNavPreferenceEnvironmentId = currentWorkspace?.id != null ? String(currentWorkspace.id) : undefined;
  const bootstrappedSideNavPreferences = auth.user?.settings?.side_nav_preferences;
  const [sideNavPreferences, setSideNavPreferences] = useState(bootstrappedSideNavPreferences);
  const sideNavPreferencesSaveIdRef = useRef(0);

  useEffect(() => {
    setSideNavPreferences(bootstrappedSideNavPreferences);
  }, [bootstrappedSideNavPreferences]);

  const updateUserSettings = useUpdateUserSettings({
    accountId: auth.account?.id,
    userId: auth.user?.id,
    mutationConfig: {
      onError: () => {
        snackbars.create({
          id: 'side-nav-preferences.update.error',
          message: 'Failed to save sidebar preferences.',
          variant: 'error',
        });
      },
    },
  });
  const updateUserSettingsMutateAsyncRef = useRef(updateUserSettings.mutateAsync);
  updateUserSettingsMutateAsyncRef.current = updateUserSettings.mutateAsync;

  const onClickNavItem = useCallback(
    (event: React.MouseEvent | undefined, item: NavItemType, section: string | null, parentItem: string | null) => {
      if (event == null) {
        return;
      }

      if (!shouldProcessLinkClick(event)) return;

      event.preventDefault();

      void track('leftNav', 'itemClicked', {
        'Nav Item': item.label,
        'Nav Section': section ?? null,
        'Is Nested': parentItem != null,
        'Parent Item': parentItem ?? null,
        'Entry Point': isCollapsedRef.current ? 'collapsed_icon' : 'expanded',
      });

      if (item.href == null) return;

      safeNavigate(item.href, item.transitionOptions ?? {});
    },
    [safeNavigate, track],
  );

  const onClickMoreMenuNavItem = useCallback<NavItemClickHandler>(
    (event, item, section, parentItem) => {
      if (event == null || !shouldProcessLinkClick(event)) {
        return;
      }

      void track('leftNav', 'moreMenuItemClicked', {
        'Item ID': item.id,
        Section: section ?? null,
        URL: item.href ?? null,
      });

      onClickNavItem(event, item, section, parentItem);
    },
    [onClickNavItem, track],
  );

  const rawNavItemGroups = useMemo(() => {
    let items: NavItemGroup[] = [];

    if (currentWorkspace != null) {
      const objectTypes = journeysObjectTypes.data ?? [];
      items = generateRoutes(currentWorkspace, auth, permissions, { taxonomy, objectTypes });
    }

    if (
      isSettingsPage &&
      auth.user?.last_cio_workspace_id != null &&
      !isEmptyString(auth.user?.last_cio_workspace_id) &&
      currentWorkspace != null
    ) {
      items = generateRoutes(currentWorkspace, auth, permissions, { taxonomy });
    }

    return items;
  }, [auth, currentWorkspace, permissions, taxonomy, isSettingsPage, journeysObjectTypes.data]);

  const { customizationGroups, hiddenItems, permanentMoreItems, permanentMorePresentationItems } = useMemo(() => {
    return getSideNavPreferenceData(rawNavItemGroups, sideNavPreferences, sideNavPreferenceEnvironmentId);
  }, [rawNavItemGroups, sideNavPreferenceEnvironmentId, sideNavPreferences]);

  const presentationNavItemGroups = useMemo(() => {
    return getPresentationNavItemGroups(
      rawNavItemGroups,
      sideNavPreferences,
      activeRoute,
      sideNavPreferenceEnvironmentId,
    );
  }, [activeRoute, rawNavItemGroups, sideNavPreferenceEnvironmentId, sideNavPreferences]);

  const openCustomizeSidebarModal = useCallback(() => {
    void track('leftNav', 'customizeModalOpened', {});

    modals.open(CustomizeSidebarModal, {
      environmentId: sideNavPreferenceEnvironmentId,
      groups: customizationGroups,
      preferences: sideNavPreferences,
      onPreferenceChange: (change) => {
        void track('leftNav', 'preferenceChanged', {
          'Item ID': change.itemId,
          Section: change.section,
          'Previous Value': change.previousValue,
          'New Value': change.newValue,
          Source: 'customize_modal',
        });
      },
      onSave: async (preferences) => {
        const saveId = sideNavPreferencesSaveIdRef.current + 1;

        sideNavPreferencesSaveIdRef.current = saveId;
        setSideNavPreferences(preferences);

        const settings = await updateUserSettingsMutateAsyncRef.current({ sideNavPreferences: preferences });

        if (sideNavPreferencesSaveIdRef.current !== saveId) {
          return preferences;
        }

        const savedPreferences = settings.side_nav_preferences ?? preferences;
        setSideNavPreferences(savedPreferences);
        return savedPreferences;
      },
    });
  }, [customizationGroups, modals, sideNavPreferenceEnvironmentId, sideNavPreferences, track]);

  const navItemGroupElements = useMemo(() => {
    return presentationNavItemGroups.map((group, index) => {
      const isGroupVisible = group.isVisibleInSection;

      return (
        <SideNavGroup
          key={group.title ?? `group_${index}`}
          aria-hidden={isGroupVisible ? undefined : true}
          inert={isGroupVisible ? undefined : true}
          isTitleVisible={isGroupVisible}
          title={group.title}
        >
          {group.items.map((item) => {
            return <ExtendedNavItem key={item.id} item={item} onClick={onClickNavItem} section={group.title ?? null} />;
          })}
        </SideNavGroup>
      );
    });
  }, [presentationNavItemGroups, onClickNavItem]);

  const isPermanentMorePresentationGroupVisible = permanentMorePresentationItems.some((item) =>
    isNavItemActive(item, activeRoute),
  );
  const permanentMorePresentationGroupElement =
    permanentMorePresentationItems.length > 0 ? (
      <SideNavGroup
        key="permanent-more-presentation-items"
        aria-hidden={isPermanentMorePresentationGroupVisible ? undefined : true}
        inert={isPermanentMorePresentationGroupVisible ? undefined : true}
      >
        {permanentMorePresentationItems.map((item) => (
          <ExtendedNavItem key={item.id} item={item} onClick={onClickNavItem} section={null} />
        ))}
      </SideNavGroup>
    ) : null;

  const moreMenuItems = [...hiddenItems, ...permanentMoreItems];
  const shouldRenderMoreMenu = moreMenuItems.length > 0 || customizationGroups.length > 0;
  const moreMenuElement = shouldRenderMoreMenu ? (
    <SideNavGroup key="side-nav-more-menu">
      <NavItemDropdown>
        <NavItemDropdownContent
          menuBefore={
            moreMenuItems.length > 0 ? (
              <DropdownMenuGroup>
                {moreMenuItems.map(({ item, section, parentItem }) => (
                  <MoreMenuNavItem
                    key={`${section ?? 'more'}_${parentItem ?? 'item'}_${item.id}`}
                    item={item}
                    onClick={onClickMoreMenuNavItem}
                    section={section}
                    parentItem={parentItem}
                  />
                ))}
              </DropdownMenuGroup>
            ) : null
          }
          menuAfter={
            <DropdownMenuGroup>
              <DropdownMenuItem icon="manage" onClick={openCustomizeSidebarModal}>
                Customize sidebar
              </DropdownMenuItem>
            </DropdownMenuGroup>
          }
        />

        <NavItemDropdownTrigger aria-label="More navigation options" icon="more">
          More
        </NavItemDropdownTrigger>
      </NavItemDropdown>
    </SideNavGroup>
  ) : null;

  return (
    <>
      {navItemGroupElements}
      {permanentMorePresentationGroupElement}
      {moreMenuElement}
    </>
  );
}
